/**
 * Dynamic Bundle & Subscription Picker JavaScript
 * Works with Shopify's native product forms and subscription APIs
 * Properly triggers cart drawer on successful add to cart
 */
class ATCBundlePicker {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find all product forms on the page
    const productForms = document.querySelectorAll(".atc-product-form");

    productForms.forEach((form) => {
      this.initializeForm(form);
    });
  }

  initializeForm(form) {
    const productId = form.dataset.productId;
    const productDataElement = document.getElementById(
      `product-data-${productId}`
    );

    if (!productDataElement) {
      console.warn("Product data not found for form:", productId);
      return;
    }

    const productData = JSON.parse(productDataElement.textContent);
    const formHandler = new ProductFormHandler(form, productData);
    formHandler.initialize();
  }
}

class ProductFormHandler {
  constructor(form, productData) {
    this.form = form;
    this.productData = productData;
    this.currentVariant = this.getCurrentVariant();

    // Set initial selling plan ID to first available subscription plan
    this.currentSellingPlanId = this.getInitialSellingPlanId();

    // Cache DOM elements
    this.refreshDOMElements();
  }

  getInitialSellingPlanId() {
    // Try to get the first selling plan from the current variant as default
    const allocations = this.currentVariant.selling_plan_allocations || [];
    if (allocations.length > 0) {
      console.log(
        "Setting initial selling plan ID to:",
        allocations[0].selling_plan_id
      );
      return allocations[0].selling_plan_id.toString();
    }
    return null;
  }

  refreshDOMElements() {
    this.addToCartButton = this.form.querySelector("#add-to-cart-button");
    this.errorMessage = this.form.querySelector("#error-message");
    this.variantRadios = this.form.querySelectorAll('input[name="id"]');
    this.variantCards = this.form.querySelectorAll(".atc-variant-card");
    this.purchaseOptionsContainer = this.form.querySelector(
      "#purchase-options-container"
    );
  }

  initialize() {
    console.log("Initializing with current variant:", this.currentVariant);

    this.bindEvents();

    // Ensure the correct variant is selected first
    this.updateVariantSelection();

    this.buildAllPurchaseOptions();
    this.updateUI();

    // Force initial update after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.updateVariantSelection();
      this.buildAllPurchaseOptions();

      // Update all variant prices and discount badges with subscription-based pricing
      this.updateAllVariantPrices();
      this.updatePricePerBottle();

      // Ensure we have the correct initial selling plan ID set
      this.ensureCorrectInitialSellingPlan();
    }, 100);
  }

  ensureCorrectInitialSellingPlan() {
    // Check if we have a subscription selected by default
    const checkedSellingPlanRadio = this.form.querySelector(
      'input[name="selling_plan"]:checked'
    );
    if (checkedSellingPlanRadio && checkedSellingPlanRadio.value !== "") {
      this.currentSellingPlanId = checkedSellingPlanRadio.value;
      console.log("Initial selling plan ID set to:", this.currentSellingPlanId);
    } else if (this.currentSellingPlanId) {
      // If we have a selling plan ID but no radio is checked, we still want to show subscription pricing
      console.log(
        "No radio checked but we have selling plan ID:",
        this.currentSellingPlanId
      );
    }

    // Always update prices to ensure everything is synchronized
    this.updatePricePerBottle();
  }

  getCurrentVariant() {
    // Always start with second variant as default, regardless of what's checked
    // The Liquid template should have set the second variant as checked already
    const defaultVariant =
      this.productData.product.variants[1] ||
      this.productData.product.variants[0];
    console.log(
      "getCurrentVariant - defaulting to second variant:",
      defaultVariant
    );
    return defaultVariant;
  }

  bindEvents() {
    // Variant selection events
    this.variantRadios.forEach((input) => {
      input.addEventListener("change", (e) => this.handleVariantChange(e));
    });

    // Variant card click events - use event delegation for better reliability
    this.form.addEventListener("click", (e) => {
      const variantCard = e.target.closest(".atc-variant-card");
      if (variantCard) {
        this.handleVariantCardClick(e, variantCard);
      }
    });

    // Form submission - USE AJAX instead of native form submission
    this.form.addEventListener("submit", (e) => this.handleFormSubmission(e));

    // Listen for Shopify variant change events
    document.addEventListener("variant:change", (e) => {
      if (e.detail.productId === this.productData.product.id) {
        this.handleVariantChangeEvent(e.detail);
      }
    });
  }

  handleVariantChange(e) {
    const variantId = parseInt(e.target.value);
    const newVariant = this.productData.product.variants.find(
      (v) => v.id === variantId
    );

    if (!newVariant) {
      console.warn("Variant not found:", variantId);
      return;
    }

    // Clear error messages
    this.hideError();

    // Update current variant
    this.currentVariant = newVariant;

    // Reset selling plan selection when variant changes
    this.currentSellingPlanId = null;

    // Update UI elements
    this.updateVariantSelection();
    this.buildAllPurchaseOptions();
    this.updateAddToCartButton();

    // Dispatch custom event for other scripts to listen
    this.dispatchVariantChangeEvent();
  }

  handleVariantCardClick(e, variantCard) {
    if (e.target.type !== "radio") {
      const radio = variantCard.querySelector('input[type="radio"]');
      if (radio && !radio.disabled) {
        radio.checked = true;
        radio.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  handlePurchaseOptionClick(e) {
    if (e.target.type !== "radio") {
      const radio = e.currentTarget.querySelector('input[type="radio"]');
      if (radio) {
        radio.checked = true;
        this.handleSellingPlanChange(radio);
      }
    }
  }

  handleSellingPlanChange(radioElement) {
    const sellingPlanId = radioElement.value;
    this.currentSellingPlanId = sellingPlanId === "" ? null : sellingPlanId;
    this.updatePurchaseOptionSelection();
    this.updatePricePerBottle();
  }

  handleFormSubmission(e) {
    e.preventDefault(); // Always prevent default - we'll handle with AJAX

    if (!this.currentVariant.available) {
      this.showError("This variant is currently out of stock");
      return;
    }

    // Show loading state
    const originalText = this.addToCartButton.textContent;
    this.addToCartButton.textContent = "Adding...";
    this.addToCartButton.disabled = true;

    // Get the selected selling plan
    const selectedSellingPlan = this.form.querySelector(
      'input[name="selling_plan"]:checked'
    );
    const sellingPlanId = selectedSellingPlan
      ? selectedSellingPlan.value
      : null;

    // Use Shopify's AJAX cart API with bundled section rendering
    this.addToCartViaAjax(this.currentVariant.id, sellingPlanId, originalText);
  }

  async addToCartViaAjax(variantId, sellingPlanId, originalButtonText) {
    try {
      const items = [
        {
          id: variantId,
          quantity: 1,
        },
      ];

      // Add selling plan if selected
      if (sellingPlanId && sellingPlanId !== "") {
        items[0].selling_plan = parseInt(sellingPlanId);
      }

      // Get sections to render (Dawn-specific approach)
      const sectionsToRender = this.getSectionsToRender();

      const body = JSON.stringify({
        items,
        sections: sectionsToRender.map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add to cart");
      }

      const result = await response.json();

      // Reset button state
      this.addToCartButton.disabled = false;
      this.addToCartButton.textContent = originalButtonText;

      // Update each section using Dawn's approach
      sectionsToRender.forEach(({ id, selector, section }) => {
        const html = result.sections[section];
        if (!html) {
          console.warn(`No HTML found for section: ${section}`);
          return;
        }

        // Special handling for cart drawer
        if (id === "CartDrawer") {
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer) {
            // Use the cart drawer's built-in method to update contents
            if (typeof cartDrawer.renderContents === "function") {
              cartDrawer.renderContents({ sections: result.sections });
            } else {
              // Fallback to manual update
              const drawerInner = cartDrawer.querySelector(".drawer__inner");
              if (drawerInner && html) {
                const temp = document.createElement("div");
                temp.innerHTML = html;
                const newContent = temp.querySelector(selector);
                if (newContent) {
                  drawerInner.innerHTML = newContent.innerHTML;
                }
              }
            }
          }
          return;
        }

        // Handle other sections
        const wrapper =
          document.getElementById(id) || document.querySelector(`#${id}`);
        if (wrapper && html) {
          // Extract only the inner part we care about
          const temp = document.createElement("div");
          temp.innerHTML = html;
          const newInner = temp.querySelector(selector);
          const currentInner = wrapper.querySelector(selector) || wrapper;
          if (newInner && currentInner) {
            currentInner.innerHTML = newInner.innerHTML;
          }
        } else {
          console.warn(`Could not find wrapper element for: ${id}`);
        }
      });

      // Update cart drawer and then open it
      setTimeout(() => {
        this.updateAndOpenCartDrawer(result);
      }, 100);

      // Show success message
      this.showSuccess("Added to cart!");

      // Dispatch Shopify cart update events for other scripts to listen
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart: result },
        })
      );

      document.dispatchEvent(
        new CustomEvent("ajaxProduct:added", {
          detail: {
            product: result.items[0],
            cartData: result,
          },
        })
      );

      // Trigger the same events that Dawn's cart system uses
      if (window.publish && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-add",
          cartData: result,
          variantId: variantId,
        });
      }
    } catch (error) {
      console.error("Cart error:", error);
      this.addToCartButton.textContent = originalButtonText;
      this.addToCartButton.disabled = false;
      this.showError(
        error.message || "Failed to add to cart. Please try again."
      );
    }
  }

  getSectionsToRender() {
    // Dawn theme section configuration
    const sections = [];

    // Cart drawer section - this is the main one we need
    sections.push({
      id: "CartDrawer",
      section: "cart-drawer",
      selector: ".drawer__inner",
    });

    // Cart icon bubble (header cart count)
    sections.push({
      id: "cart-icon-bubble",
      section: "cart-icon-bubble",
      selector: ".shopify-section",
    });

    // Main cart items (if on cart page)
    const mainCartItems = document.getElementById("main-cart-items");
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push({
        id: "main-cart-items",
        section: mainCartItems.dataset.id,
        selector: ".js-contents",
      });
    }

    // Cart footer (if on cart page)
    const mainCartFooter = document.getElementById("main-cart-footer");
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push({
        id: "main-cart-footer",
        section: mainCartFooter.dataset.id,
        selector: ".js-contents",
      });
    }

    // Try to find header section for cart count update
    const headerElement = document.querySelector("header[data-section-id]");
    if (headerElement) {
      sections.push({
        id: headerElement.id || "shopify-section-header",
        section: headerElement.dataset.sectionId,
        selector: ".header-wrapper",
      });
    }

    return sections;
  }

  handleVariantChangeEvent(detail) {
    this.currentVariant = detail.variant;
    this.updateUI();
    this.buildAllPurchaseOptions();
  }

  updateUI() {
    this.updateVariantSelection();
    this.updateAddToCartButton();
  }

  updateVariantSelection() {
    console.log(
      "updateVariantSelection - current variant:",
      this.currentVariant
    );

    // Re-query variant cards in case DOM has changed
    const variantCards = this.form.querySelectorAll(".atc-variant-card");
    console.log("updateVariantSelection - found cards:", variantCards.length);

    // Update variant card states
    variantCards.forEach((card) => {
      const variantId = parseInt(card.dataset.variantId);
      console.log(
        `updateVariantSelection - checking card variant ${variantId} vs current ${this.currentVariant.id}`
      );

      if (variantId === this.currentVariant.id) {
        card.classList.add("selected");
        // Also update the radio button
        const radio = card.querySelector('input[type="radio"]');
        if (radio) {
          radio.checked = true;
          console.log(`updateVariantSelection - selected variant ${variantId}`);
        }
      } else {
        card.classList.remove("selected");
        const radio = card.querySelector('input[type="radio"]');
        if (radio) {
          radio.checked = false;
        }
      }
    });
  }

  buildAllPurchaseOptions() {
    if (!this.purchaseOptionsContainer) {
      console.warn("Purchase options container not found");
      return;
    }

    // Clear all existing purchase options
    this.purchaseOptionsContainer.innerHTML = "";

    // Get unique selling plans for current variant to avoid duplicates
    const uniqueSellingPlans = this.getUniqueSellingPlansForVariant();

    // Always add one-time purchase option first (unless product requires selling plan)
    if (!this.productData.product.requires_selling_plan) {
      this.addOnetimePurchaseOption();
    }

    // Add subscription options for current variant
    if (uniqueSellingPlans.length > 0) {
      this.addSubscriptionOptions(uniqueSellingPlans);
    }

    // Update selection states
    this.updatePurchaseOptionSelection();

    // Update price per bottle after options are built
    setTimeout(() => {
      this.updatePricePerBottle();
    }, 50);
  }

  getUniqueSellingPlansForVariant() {
    // Get selling plan allocations for current variant
    const allocations = this.currentVariant.selling_plan_allocations || [];

    // Create a map to ensure uniqueness by selling_plan_id
    const uniquePlansMap = new Map();

    allocations.forEach((allocation) => {
      if (!uniquePlansMap.has(allocation.selling_plan_id)) {
        uniquePlansMap.set(allocation.selling_plan_id, allocation);
      }
    });

    return Array.from(uniquePlansMap.values());
  }

  addOnetimePurchaseOption() {
    // Get unique selling plans for current variant to check if subscriptions are available
    const uniqueSellingPlans = this.getUniqueSellingPlansForVariant();

    // Determine if one-time should be selected by default
    // Only select one-time if no subscriptions are available and no specific selling plan is set
    const shouldSelectOnetime =
      !this.currentSellingPlanId &&
      !this.productData.product.requires_selling_plan &&
      uniqueSellingPlans.length === 0;

    const optionHtml = `
          <label class="atc-purchase-option ${
            shouldSelectOnetime ? "selected" : ""
          }" for="onetime-purchase">
            <input type="radio" 
                  name="selling_plan" 
                  id="onetime-purchase" 
                  value="" 
                  ${shouldSelectOnetime ? "checked" : ""}
                  ${!this.currentVariant.available ? "disabled" : ""}>
            <div class="atc-purchase-content">
              <div class="atc-purchase-details">
                <h4>${this.productData.settings.onetime_label}</h4>
                <div class="atc-purchase-pricing">
                  <span class="variant-price">${this.formatMoney(
                    this.currentVariant.price
                  )}</span>
                  ${
                    this.currentVariant.compare_at_price >
                    this.currentVariant.price
                      ? `<span style="font-size:1.2rem;text-decoration: line-through; color: #64748b; margin-left: 8px;">${this.formatMoney(
                          this.currentVariant.compare_at_price
                        )}</span>`
                      : ""
                  }
                </div>
              </div>
            </div>
          </label>
        `;

    this.purchaseOptionsContainer.insertAdjacentHTML("beforeend", optionHtml);
  }

  addSubscriptionOptions(uniqueSellingPlans) {
    if (uniqueSellingPlans.length === 0) {
      return;
    }

    let isFirstSubscription = true;
    const hasOnetimeOption = !this.productData.product.requires_selling_plan;

    uniqueSellingPlans.forEach((allocation, index) => {
      // Calculate savings compared to one-time price
      const savings = this.currentVariant.price - allocation.price;

      // Determine if this option should be selected
      // Default to first subscription if no specific selling plan is set
      const isSelected =
        this.currentSellingPlanId === allocation.selling_plan_id.toString() ||
        (!this.currentSellingPlanId && isFirstSubscription);

      // Create more descriptive titles
      let title = "Subscribe & Save";
      let subtitle = this.parseSubscriptionFrequency(
        allocation.selling_plan_name
      );

      // If we couldn't parse frequency and have multiple plans, differentiate them
      if (!subtitle && uniqueSellingPlans.length > 1) {
        subtitle = allocation.selling_plan_name;
      }

      const optionHtml = `
            <label class="atc-purchase-option ${
              isSelected ? "selected" : ""
            }" for="selling-plan-${allocation.selling_plan_id}">
              <input type="radio" 
                    name="selling_plan" 
                    id="selling-plan-${allocation.selling_plan_id}" 
                    value="${allocation.selling_plan_id}"
                    ${isSelected ? "checked" : ""}
                    ${!this.currentVariant.available ? "disabled" : ""}>
              <div class="atc-purchase-content">
                <div class="atc-purchase-details">
                  <h4>${title}</h4>
                  ${
                    subtitle
                      ? `<div style="font-size: 1.2rem; color: #64748b; margin-bottom: 4px;">${subtitle}</div>`
                      : ""
                  }
                  <div class="atc-purchase-pricing">
                    ${
                      this.currentVariant.price > allocation.price
                        ? `<span style="text-decoration: line-through; color: #64748b; margin-right: 8px;">${this.formatMoney(
                            this.currentVariant.price
                          )}</span>`
                        : ""
                    }
                    <span>${this.formatMoney(allocation.price)}</span>
                  </div>
                </div>
                ${
                  savings > 0
                    ? `<div class="atc-savings-badge">${
                        this.productData.settings.save_text
                      } an extra ${this.formatMoney(savings)}</div>`
                    : ""
                }
              </div>
            </label>
          `;

      this.purchaseOptionsContainer.insertAdjacentHTML("beforeend", optionHtml);

      // Track if this option is selected and set as current selling plan
      if (isSelected) {
        this.currentSellingPlanId = allocation.selling_plan_id.toString();
      }

      if (isFirstSubscription) {
        isFirstSubscription = false;
      }
    });

    // Add event listeners to all purchase options
    this.addPurchaseOptionEventListeners();
  }

  parseSubscriptionFrequency(sellingPlanName) {
    const name = sellingPlanName.toLowerCase();

    // Parse monthly subscriptions
    const monthMatch = name.match(/(\d+)\s*month/i);
    if (monthMatch) {
      const months = parseInt(monthMatch[1]);
      return months === 1
        ? "Delivered monthly"
        : `Delivered every ${months} months`;
    }

    // Parse weekly subscriptions
    const weekMatch = name.match(/(\d+)\s*week/i);
    if (weekMatch) {
      const weeks = parseInt(weekMatch[1]);
      return weeks === 1
        ? "Delivered weekly"
        : `Delivered every ${weeks} weeks`;
    }

    // Parse daily subscriptions
    const dayMatch = name.match(/(\d+)\s*day/i);
    if (dayMatch) {
      const days = parseInt(dayMatch[1]);
      return `Delivered every ${days} days`;
    }

    // Return original name if we can't parse it
    return sellingPlanName;
  }

  addPurchaseOptionEventListeners() {
    // Remove old event listeners to prevent duplicates
    this.purchaseOptionsContainer
      .querySelectorAll(".atc-purchase-option")
      .forEach((option) => {
        // Clone to remove all event listeners
        const newOption = option.cloneNode(true);
        option.parentNode.replaceChild(newOption, option);
      });

    // Add fresh event listeners
    this.purchaseOptionsContainer
      .querySelectorAll(".atc-purchase-option")
      .forEach((option) => {
        option.addEventListener("click", (e) =>
          this.handlePurchaseOptionClick(e)
        );
      });

    this.purchaseOptionsContainer
      .querySelectorAll('input[name="selling_plan"]')
      .forEach((input) => {
        input.addEventListener("change", (e) =>
          this.handleSellingPlanChange(e.target)
        );
      });
  }

  updatePurchaseOptionSelection() {
    // Update visual states for all purchase options
    const allPurchaseOptions = this.form.querySelectorAll(
      ".atc-purchase-option"
    );
    allPurchaseOptions.forEach((option) => {
      option.classList.remove("selected");
    });

    const checkedRadio = this.form.querySelector(
      'input[name="selling_plan"]:checked'
    );
    if (checkedRadio) {
      checkedRadio.closest(".atc-purchase-option").classList.add("selected");
    }
  }

  updatePricePerBottle() {
    console.log(
      "updatePricePerBottle called - Selling plan:",
      this.currentSellingPlanId
    );

    // Update prices only for the CURRENT variant, not all variants
    this.updateCurrentVariantPrices();

    // Dispatch event for other components that might need to update
    document.dispatchEvent(
      new CustomEvent("pricePerBottle:updated", {
        detail: {
          variantId: this.currentVariant.id,
          sellingPlanId: this.currentSellingPlanId,
        },
      })
    );
  }

  updateCurrentVariantPrices() {
    // Find price per bottle element for CURRENT variant only
    const priceElement = document.querySelector(
      `.atc-price-per-bottle[data-variant-id="${this.currentVariant.id}"]`
    );

    if (!priceElement) {
      console.warn(
        `Price element not found for current variant ${this.currentVariant.id}`
      );
      return;
    }

    const bottleCount = parseInt(priceElement.dataset.bottleCount) || 1;

    // Calculate effective price for current variant based on current selling plan selection
    const effectivePrice = this.getEffectivePriceForVariant(
      this.currentVariant
    );
    const pricePerBottle = effectivePrice / bottleCount;

    console.log(
      `Current variant ${this.currentVariant.id}: ${this.formatMoney(
        effectivePrice
      )} / ${bottleCount} = ${this.formatMoney(pricePerBottle)}/bottle`
    );

    // Update the price amount span
    const priceAmountSpan = priceElement.querySelector(".price-amount");
    if (priceAmountSpan) {
      priceAmountSpan.textContent = this.formatMoney(pricePerBottle);
    }

    // Calculate percentage savings from compare_at_price to current effective price
    this.updateSavingsDisplay(priceElement, effectivePrice, bottleCount);
  }

  updateSavingsDisplay(priceElement, effectivePrice, bottleCount) {
    // Find the discount badge in the current variant's card
    const currentVariantCard = document.querySelector(
      `.atc-variant-card[data-variant-id="${this.currentVariant.id}"]`
    );
    const discountBadge = currentVariantCard
      ? currentVariantCard.querySelector(".atc-discount-badge")
      : null;

    // Use compare_at_price as the base for percentage calculation
    const compareAtPrice =
      this.currentVariant.compare_at_price || this.currentVariant.price;

    if (effectivePrice < compareAtPrice && discountBadge) {
      const savingsAmount = compareAtPrice - effectivePrice;
      const savingsPercentage = Math.round(
        (savingsAmount / compareAtPrice) * 100
      );

      // Update the discount badge next to the title
      discountBadge.textContent = `-${savingsPercentage}%`;
      discountBadge.style.display = "inline-block";

      console.log(
        `Updated discount badge: ${savingsPercentage}% savings from compare price ${this.formatMoney(
          compareAtPrice
        )} to effective price ${this.formatMoney(effectivePrice)}`
      );
    } else if (discountBadge) {
      // Hide discount badge when no discount or reset to original if needed
      if (this.currentVariant.compare_at_price > this.currentVariant.price) {
        // Show original discount if there's a base sale
        const originalSavings =
          this.currentVariant.compare_at_price - this.currentVariant.price;
        const originalPercentage = Math.round(
          (originalSavings / this.currentVariant.compare_at_price) * 100
        );
        discountBadge.textContent = `-${originalPercentage}%`;
        discountBadge.style.display = "inline-block";
      } else {
        discountBadge.style.display = "none";
      }
    }
  }

  updateAllVariantPrices() {
    // Keep this method for initial load - updates all variants with their default subscription pricing
    const allPriceElements = document.querySelectorAll(
      ".atc-price-per-bottle[data-variant-id]"
    );

    console.log(
      `Found ${allPriceElements.length} price elements to update (initial load)`
    );

    allPriceElements.forEach((priceElement) => {
      const variantId = parseInt(priceElement.dataset.variantId);
      const bottleCount = parseInt(priceElement.dataset.bottleCount) || 1;

      // Find this variant in our product data
      const variant = this.productData.product.variants.find(
        (v) => v.id === variantId
      );
      if (!variant) {
        console.warn(`Variant ${variantId} not found in product data`);
        return;
      }

      // For initial load, always show subscription pricing if available
      let initialPrice = variant.price;
      const allocations = variant.selling_plan_allocations || [];
      if (allocations.length > 0) {
        initialPrice = allocations[0].price;
      }

      const pricePerBottle = initialPrice / bottleCount;

      // Update the price amount span
      const priceAmountSpan = priceElement.querySelector(".price-amount");
      if (priceAmountSpan) {
        priceAmountSpan.textContent = this.formatMoney(pricePerBottle);
      }

      // Update the discount badge for this variant if it has subscription savings
      const variantCard = document.querySelector(
        `.atc-variant-card[data-variant-id="${variantId}"]`
      );
      const discountBadge = variantCard
        ? variantCard.querySelector(".atc-discount-badge")
        : null;

      if (discountBadge) {
        const compareAtPrice = variant.compare_at_price || variant.price;
        if (initialPrice < compareAtPrice) {
          const savingsAmount = compareAtPrice - initialPrice;
          const savingsPercentage = Math.round(
            (savingsAmount / compareAtPrice) * 100
          );
          discountBadge.textContent = `-${savingsPercentage}%`;
          discountBadge.style.display = "inline-block";
        }
      }
    });
  }

  getEffectivePriceForVariant(variant) {
    // Check what's currently selected in the form to ensure dynamic updates
    const checkedSellingPlanRadio = this.form.querySelector(
      'input[name="selling_plan"]:checked'
    );

    let effectiveSellingPlanId = null;

    if (checkedSellingPlanRadio) {
      effectiveSellingPlanId =
        checkedSellingPlanRadio.value === ""
          ? null
          : checkedSellingPlanRadio.value;
    } else if (this.currentSellingPlanId) {
      // Use the stored selling plan ID if no radio is checked yet (initial load)
      effectiveSellingPlanId = this.currentSellingPlanId;
    } else {
      // Default to first subscription if available for initial load
      const allocations = variant.selling_plan_allocations || [];
      if (allocations.length > 0) {
        effectiveSellingPlanId = allocations[0].selling_plan_id.toString();
      }
    }

    // If no selling plan is selected, return the regular variant price
    if (!effectiveSellingPlanId) {
      return variant.price;
    }

    // Find the selected selling plan allocation for this variant
    const allocations = variant.selling_plan_allocations || [];
    const selectedAllocation = allocations.find(
      (allocation) =>
        allocation.selling_plan_id.toString() === effectiveSellingPlanId
    );

    // If we can't find the exact selling plan for this variant, try to find any subscription plan
    if (!selectedAllocation && allocations.length > 0) {
      console.log(
        `Selling plan ${effectiveSellingPlanId} not found for variant ${variant.id}, using first available subscription`
      );
      return allocations[0].price;
    }

    // Return the selling plan price if found, otherwise fall back to variant price
    return selectedAllocation ? selectedAllocation.price : variant.price;
  }

  getCurrentEffectivePrice() {
    return this.getEffectivePriceForVariant(this.currentVariant);
  }

  updateAddToCartButton() {
    if (this.currentVariant.available) {
      this.addToCartButton.disabled = false;
      this.addToCartButton.textContent =
        this.productData.settings.add_to_cart_text;
    } else {
      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent =
        this.productData.settings.sold_out_text;
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(this.productData.settings.locale, {
      style: "currency",
      currency: this.productData.settings.currency,
    }).format(cents / 100);
  }

  showError(message) {
    this.errorMessage.textContent = message;
    this.errorMessage.style.display = "block";
    this.errorMessage.style.color = "#dc2626";
    this.errorMessage.style.fontSize = "0.875rem";
    this.errorMessage.style.marginTop = "8px";
  }

  hideError() {
    this.errorMessage.textContent = "";
    this.errorMessage.style.display = "none";
  }

  showSuccess(message) {
    this.errorMessage.textContent = message;
    this.errorMessage.style.display = "block";
    this.errorMessage.style.color = "#059669";
    this.errorMessage.style.fontSize = "0.875rem";
    this.errorMessage.style.marginTop = "8px";

    // Hide success message after 3 seconds
    setTimeout(() => {
      this.hideError();
    }, 3000);
  }

  dispatchVariantChangeEvent() {
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: {
          variant: this.currentVariant,
          product: this.productData.product,
          productId: this.productData.product.id,
        },
      })
    );
  }

  openCartDrawer() {
    console.log("Attempting to open cart drawer...");

    // Method 1: Try Dawn theme cart drawer
    const drawer = document.querySelector("cart-drawer");
    console.log("Found cart-drawer element:", drawer);

    if (drawer) {
      console.log(
        "Available methods on drawer:",
        Object.getOwnPropertyNames(drawer.__proto__)
      );

      // Remove empty state classes that might prevent proper display
      drawer.classList.remove("is-empty");
      const drawerInner = drawer.querySelector(".drawer__inner");
      if (drawerInner) {
        drawerInner.classList.remove("is-empty");
      }

      if (typeof drawer.open === "function") {
        console.log("Opening drawer using .open() method");
        drawer.open();
        return;
      }

      // Method 2: Try triggering click on cart icon/button
      const cartIcon = document.querySelector(
        ".cart-count-bubble, .cart-drawer-toggle, [data-cart-drawer-toggle]"
      );
      if (cartIcon) {
        cartIcon.click();
        return;
      }

      // Method 3: Dispatch custom event that cart might be listening for
      document.dispatchEvent(new CustomEvent("cart:open"));

      // Method 4: Try to show drawer by manipulating classes
      drawer.classList.add("active", "open");
      drawer.style.visibility = "visible";
      document.body.classList.add("cart-drawer-open");

      return;
    }

    // Method 5: Look for other cart drawer implementations
    const cartDrawerAlternatives = [
      ".cart-drawer",
      "#cart-drawer",
      "[data-cart-drawer]",
      ".drawer--cart",
    ];

    for (const selector of cartDrawerAlternatives) {
      const element = document.querySelector(selector);
      if (element) {
        // Try various opening methods
        if (typeof element.open === "function") {
          element.open();
        } else if (typeof element.show === "function") {
          element.show();
        } else {
          element.classList.add("active", "open", "is-open");
          element.style.visibility = "visible";
          element.style.display = "block";
        }
        return;
      }
    }

    // Method 6: Try to trigger existing cart functionality
    const existingCartButtons = document.querySelectorAll(
      "button[data-cart], .cart-button, .header-cart, .cart-icon"
    );

    for (const button of existingCartButtons) {
      if (
        button.getAttribute("aria-expanded") === "false" ||
        button.getAttribute("aria-controls")
      ) {
        button.click();
        return;
      }
    }

    // Method 7: Final fallback - redirect to cart page
    console.warn("Cart drawer could not be opened, falling back to cart page");
    // Uncomment the line below if you want to redirect to cart as fallback
    // window.location.href = '/cart';
  }

  async updateAndOpenCartDrawer(cartData) {
    console.log("Updating and opening cart drawer with data:", cartData);

    const drawer = document.querySelector("cart-drawer");
    if (!drawer) {
      console.warn("Cart drawer element not found");
      return;
    }

    try {
      // Method 1: Try to fetch fresh cart drawer content
      const response = await fetch(
        `${window.location.pathname}?section_id=cart-drawer`
      );
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newDrawerContent = doc.querySelector("#CartDrawer");

        if (newDrawerContent) {
          const currentDrawerContent = drawer.querySelector("#CartDrawer");
          if (currentDrawerContent) {
            // Replace the cart drawer content
            currentDrawerContent.innerHTML = newDrawerContent.innerHTML;
            console.log("Cart drawer content updated via fetch");
          }
        }
      }
    } catch (error) {
      console.warn("Failed to fetch cart drawer content:", error);
    }

    // Remove empty state classes
    drawer.classList.remove("is-empty");
    const drawerInner = drawer.querySelector(".drawer__inner");
    if (drawerInner) {
      drawerInner.classList.remove("is-empty");
    }

    // Also update cart drawer items if they exist
    const cartDrawerItems = drawer.querySelector("cart-drawer-items");
    if (cartDrawerItems) {
      cartDrawerItems.classList.remove("is-empty");
    }

    // Now open the drawer
    this.openCartDrawer();
  }
}

// Initialize when DOM is ready
new ATCBundlePicker();
